import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import client from '../api/client';
import { reachabilityMonitor } from '../offline/reachability';
import {
  clearLocalData as clearOfflineData,
  getSyncStatus,
  setupAutoSync,
  subscribeSyncStatus,
  syncNow as offlineSyncNow,
  type SyncStatus,
} from '../offline';
import type { RootState } from './index';

interface SyncState extends SyncStatus {
  isOnline: boolean;
  isOffline: boolean;
}

const initialState: SyncState = {
  pendingCount: 0,
  lastSyncAt: null,
  isSyncing: false,
  lastSyncError: null,
  isOnline: reachabilityMonitor.isReachable,
  isOffline: reachabilityMonitor.isConfirmedOffline(),
};

export const syncNow = createAsyncThunk('sync/syncNow', async () => {
  await offlineSyncNow(client);
});

export const clearLocalData = createAsyncThunk('sync/clearLocalData', async () => {
  await clearOfflineData();
});

const syncSlice = createSlice({
  name: 'sync',
  initialState,
  reducers: {
    setSyncStatus(state, action: { payload: SyncStatus }) {
      state.pendingCount = action.payload.pendingCount;
      state.lastSyncAt = action.payload.lastSyncAt;
      state.isSyncing = action.payload.isSyncing;
      state.lastSyncError = action.payload.lastSyncError;
    },
    setReachability(
      state,
      action: { payload: { isReachable: boolean; isConfirmedOffline: boolean } },
    ) {
      state.isOnline = action.payload.isReachable;
      state.isOffline = action.payload.isConfirmedOffline;
    },
    resetSync(state) {
      state.pendingCount = 0;
      state.lastSyncAt = null;
      state.isSyncing = false;
      state.lastSyncError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(syncNow.pending, (state) => {
        state.isSyncing = true;
        state.lastSyncError = null;
      })
      .addCase(syncNow.fulfilled, (state) => {
        state.isSyncing = false;
      })
      .addCase(syncNow.rejected, (state, action) => {
        state.isSyncing = false;
        state.lastSyncError = action.error.message ?? 'Sync failed';
      });
  },
});

export const { setSyncStatus, setReachability, resetSync } = syncSlice.actions;
export default syncSlice.reducer;

export const selectSync = (state: RootState) => state.sync;

export async function loadInitialSyncStatus(
  dispatch: (action: ReturnType<typeof setSyncStatus>) => void,
): Promise<() => void> {
  const status = await getSyncStatus();
  dispatch(setSyncStatus(status));

  return subscribeSyncStatus((next: SyncStatus) => dispatch(setSyncStatus(next)));
}

export function setupSyncEngine(): () => void {
  if (reachabilityMonitor.isReachable) {
    void offlineSyncNow(client);
  }
  return setupAutoSync(client);
}
